import { useQueryClient } from '@tanstack/react-query';

import { LoginId } from '@suntex.fim/api-client';
import { useFramework } from '@suntex.fim/framework';

/**
 * Provides API-bound message actions. Components/pages consume these and
 * handle any local state updates (unread flags, selection, etc.) themselves.
 */
export function useMessageActions() {
  const { api } = useFramework();
  const queryClient = useQueryClient();

  async function markAsRead(notificationId: number): Promise<void> {
    await api.post_Notification_MarkRead({ notificationId });
  }

  /**
   * Deletes one or more notifications.
   *
   * Bug fix: the original TeacherMessages used `loginId` for both inbox and
   * outbox delete — the outbox path should use the sender's loginId (which in
   * practice is the same login, so the fix is mostly for clarity).
   */
  async function deleteMessages(
    notificationIds: number | number[],
    loginId: LoginId,
  ): Promise<void> {
    const ids = Array.isArray(notificationIds)
      ? notificationIds
      : [notificationIds];
    await api.post_Notification_Delete({ loginId, notificationIds: ids });
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['notifications', 'inbox', loginId],
      }),
      queryClient.invalidateQueries({
        queryKey: ['notifications', 'outbox', loginId],
      }),
      queryClient.invalidateQueries({
        queryKey: ['notificationCount', loginId],
      }),
    ]);
  }

  return { markAsRead, deleteMessages };
}
